/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Gift, CheckCircle2, Phone, Mail, MapPin, Target } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export const SeminarSection = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [nextSeminar, setNextSeminar] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    careerGuidance: 'Yes'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate next Saturday at 11 AM IST
  const getNextSaturday = () => {
    const now = new Date();
    
    // Convert to IST
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const istNow = new Date(now.getTime() + istOffset);
    
    const currentDay = istNow.getUTCDay();
    const daysUntilSaturday = (6 - currentDay + 7) % 7 || 7;
    
    const nextSat = new Date(istNow);
    nextSat.setUTCDate(istNow.getUTCDate() + daysUntilSaturday);
    nextSat.setUTCHours(11, 0, 0, 0); // 11:00 AM IST
    
    return nextSat;
  };

  // Initialize next seminar date
  useEffect(() => {
    const next = getNextSaturday();
    setNextSeminar(next);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!nextSeminar) return;

    const timer = setInterval(() => {
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istNow = new Date(now.getTime() + istOffset);
      
      const distance = nextSeminar.getTime() - istNow.getTime();

      if (distance < 0) {
        const next = getNextSaturday();
        setNextSeminar(next);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(timer);
  }, [nextSeminar]);

  // Format date for display in IST
  const formatSeminarDate = (date) => {
    if (!date) return '';
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata',
      timeZoneName: 'short'
    };
    return date.toLocaleString('en-IN', options);
  };

  // Add to calendar
  const addToCalendar = () => {
    if (!nextSeminar) return;

    const title = encodeURIComponent('MatchFrame Editing School - Career & Software Guidance Seminar');
    const details = encodeURIComponent('Learn about video editing software, career opportunities, and get personalized guidance from industry experts.');
    const location = encodeURIComponent('MatchFrame Editing School, Bangalore');
    
    // Format date for Google Calendar (YYYYMMDDTHHMMSS)
    const startDate = nextSeminar.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const endTime = new Date(nextSeminar.getTime() + 2 * 60 * 60 * 1000); // +2 hours
    const endDate = endTime.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    // Google Calendar URL
    const googleCalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${details}&location=${location}&sf=true&output=xml`;

    // Open in new tab
    window.open(googleCalUrl, '_blank');
    
    toast.success('Opening calendar...', {
      description: 'Add this event to your calendar and set reminders!'
    });
  };

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation
  const isValidPhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!isValidEmail(formData.email)) {
      toast.error('Invalid Email', {
        description: 'Please enter a valid email address.'
      });
      return;
    }

    // Validate phone
    if (!isValidPhone(formData.phone)) {
      toast.error('Invalid Phone Number', {
        description: 'Please enter a valid 10-digit phone number.'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/seminar-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Thank you! 🎉', {
          description: 'A representative from our team will contact you shortly.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          careerGuidance: 'Yes'
        });
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      toast.error('Registration Failed', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="seminar" className="py-8 bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50 relative overflow-hidden">
      {/* Animated background elements - Purple theme */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-40 h-40 bg-purple-400/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 bg-indigo-400/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-violet-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute bottom-1/4 right-1/3 w-36 h-36 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-white via-purple-50/40 to-indigo-50/40 border-2 border-purple-300/50 shadow-2xl rounded-2xl p-5 relative overflow-hidden">
          {/* Animated border glow - Purple gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-indigo-500/20 to-violet-500/20 rounded-2xl blur-xl animate-pulse"></div>
          
          <div className="relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Left: Info */}
            <div className="flex-1 min-w-[250px]">
              <div className="inline-block mb-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 rounded-full">
                <span className="text-orange-600 font-semibold text-xs">FREE • SAT 11 AM IST</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-1">Ultimate Video/Film Editing Seminar</h3>
              <p className="text-slate-600 text-xs mb-2">Every Saturday at 11:00 AM IST</p>
              <p className="text-purple-600 text-xs font-medium mb-3">Career Paths • Pick the best editing software • Industry Insights</p>
              <div className="flex gap-2 mb-3">
                {[
                  { label: 'D', value: timeLeft.days },
                  { label: 'H', value: timeLeft.hours },
                  { label: 'M', value: timeLeft.minutes },
                  { label: 'S', value: timeLeft.seconds }
                ].map((item, idx) => (
                  <div key={idx} className="bg-purple-50 border border-purple-200 rounded px-2 py-1 text-center">
                    <div className="text-lg font-bold text-purple-600 leading-none">{String(item.value).padStart(2, '0')}</div>
                    <div className="text-slate-500 text-xs">{item.label}</div>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 text-xs">20 slots • 2 hours • Free refreshments</p>
            </div>

            {/* Right: Quick Form */}
            <div className="flex-1 min-w-[250px]">
              <form onSubmit={handleSubmit} className="space-y-2">
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 text-xs focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="Name"
                />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 text-xs focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                  placeholder="Email"
                />
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full sm:flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 text-xs focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    placeholder="Phone"
                  />
                  <input
                    type="text"
                    required
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full sm:flex-1 px-3 py-2 bg-slate-50 border border-slate-300 rounded text-slate-900 text-xs focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    placeholder="City"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-orange-600 to-pink-600 hover:from-orange-700 hover:to-pink-700 text-white text-xs py-2 rounded mb-2"
                >
                  {isSubmitting ? 'Registering...' : 'Register Now'}
                </Button>
                <Button
                  type="button"
                  onClick={addToCalendar}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs py-2 rounded flex items-center justify-center gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Add to Calendar</span>
                </Button>
              </form>
            </div>
          </div>
          </div>
        </div>
      </div>
    </section>
  );
};
